import type { CheckoutSession } from 'models/checkoutSession';
import { fetchJson, fetchWithAuthentication } from 'utils/fetchJson/fetchJson';
import { createMapper } from './mappers/create';

export type CheckoutSessionType =
  | 'card_tokenization_for_installment'
  | 'card_tokenization_for_one_off'
  | 'direct_debit_tokenization_for_installment'
  | 'new_mandate'
  | 'one_off_for_cards';

export type DirectDebitProcessor = 'BMOC' | 'CRBA' | 'FINA' | 'SEPA';

export type PayorDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  state: string;
  zip: string;
};

export type MandateAch = {
  initDate: string;
  finalDate: string;
  numberOfPayments: number;
};

export type CreateSessionPayload = {
  sessionType: CheckoutSessionType;
  paymentMethodToken?: string;
  mandateAch?: MandateAch;
  orderId?: string;
  orderToken?: string;
  recurringReceivableId?: string;
  consumerSessionId?: string;
  captchaResponse?: string;
};

export type ConfirmSessionPayload = {
  sessionId: string;
  orderId?: string;
  orderToken?: string;
};

export type CreateSessionParams = CreateSessionPayload & {
  accessToken?: string;
  locale: string;
};

export type SessionConfirmationResponse = {
  paymentId: string | null;
  decision: 'charge' | 'surcharge';
};

type SessionsService = {
  create: (params: CreateSessionParams) => Promise<CheckoutSession>;
  confirm: (
    params: ConfirmSessionPayload,
  ) => Promise<SessionConfirmationResponse>;
};

export const sessions: SessionsService = {
  create: async ({ accessToken, locale, ...rest }) => {
    const requestOptions = {
      method: 'POST',
      body: JSON.stringify(createMapper.mapToBackend(rest)),
    };
    const options = {} as Record<string, unknown>;

    return fetchWithAuthentication(`/sessions?locale=${locale}`, {
      ...requestOptions,
      ...options,
    });
  },

  confirm: async ({
    sessionId,
    ...params
  }: ConfirmSessionPayload): Promise<SessionConfirmationResponse> => {
    return fetchJson(`/sessions/${sessionId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({
        order_id: params.orderId,
        order_token: params.orderToken,
      }),
    });
  },
};
